<script setup lang="ts"></script>
<template>
    <ContentPage title="Toegankelijkheidsverklaring Fleequid">
        <section class="mb-8">
            <p class="mt-3 text-base">
                Fleequid (Adorea SRL) verbindt zich ertoe zijn website toegankelijk te maken, in overeenstemming met de wet van 9 januari 2004, nr. 4.
                Deze toegankelijkheidsverklaring is van toepassing op:
            </p>
            <p class="mt-2">
                <NuxtLink class="text-primary hover:underline" to="https://fleequid.com" target="_blank" external>https://fleequid.com</NuxtLink>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Toegankelijkheid</h2>
            <p class="mt-2">
                Ons doel is om digitale inhoud toegankelijk te maken voor iedereen, inclusief mensen met een beperking. We zetten ons in om de
                gebruiksvriendelijkheid voortdurend te verbeteren en de beste praktijken op het gebied van toegankelijkheid toe te passen, zodat alle
                gebruikers gelijke en inclusieve toegang hebben.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Toegankelijkheidsverklaring</h2>
            <p class="mt-2">
                In overeenstemming met de geldende wetgeving en in lijn met onze waarden van inclusiviteit en gelijke kansen, hebben wij een officiële
                Toegankelijkheidsverklaring opgesteld. Dit document beschrijft de toegankelijkheidsstatus van onze website en de stappen die wij nemen
                om te voldoen aan de “Web Content Accessibility Guidelines” (WCAG).U kunt de volledige verklaring bekijken en downloaden via de
                onderstaande link:
            </p>
            <p class="mt-2">
                <a class="text-primary hover:underline" href="/docs/accessibility/accessibility-statement_NL.pdf" target="_blank">
                    Toegankelijkheidsverklaring Fleequid
                </a>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Conformiteitsstatus</h2>
            <p class="mt-2">Deels conform</p>
            <p class="mt-3">
                Deze website voldoet gedeeltelijk aan de eisen van bijlage A van de norm UNI CEI EN 301549 vanwege de onderstaande
                niet-conformiteitsgevallen.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Niet-toegankelijke inhoud</h2>
            <p class="mt-3">De hieronder vermelde inhoud is niet toegankelijk wegens schending van de wet van 9 januari 2004, nr. 4.:</p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Wat webpagina's betreft</h3>
            <h4 class="text-highlighted mt-3 font-medium">Algemeen:</h4>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>1.3.1 Informatie en verbanden (Level A)</li>
                <li>1.4.3 Minimale contrast (Level AA)</li>
                <li>1.4.4 Tekstgrootte aanpassen (Level AA)</li>
                <li>1.4.10 Herberekening van de stroom (alleen level AA 2.1)</li>
                <li>2.4.1 Bloksprongen (Level A)</li>
                <li>2.4.4 Doel van de link (in context) (Level A)</li>
                <li>2.4.5 Verschillende modi (Level AA)</li>
                <li>3.3.1 Foutidentificatie (Level A)</li>
                <li>4.1.2 Naam, rol, waarde (Level A)</li>
                <li>4.1.3 Statusberichten (Level AA wcag 2.1)</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Opstellen van de toegankelijkheidsverklaring</h2>
            <p class="mt-3">Deze verklaring is opgesteld op<time datetime="2025-08-20" class="font-bold">20 augustus 2025</time>.</p>
            <p class="mt-2">
                De informatie in deze toegankelijkheidsverklaring is verkregen uit een beoordeling uitgevoerd door derden, met behulp van automatische
                validatiesystemen en door directe verificatie, uitgevoerd door de technische toegankelijkheidsexpert.
            </p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Gewerkte tools voor automatische validatie:</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Deque Axe Accessibility Checker (extensies voor Firefox en Chrome)</li>
                <li>Siteimprove Accessibility Checker</li>
                <li>WCAG kleurcontrastchecker</li>
            </ul>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Hulpmiddelen:</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Screen reader: NVDA</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Feedback en contactinformatie</h2>
            <p class="mt-3">U kunt op elk moment meldingen rechtstreeks versturen naar het e-mailadres: <FleequidEmailLink type="accessibility" /></p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Wijze van indienen van meldingen aan AgID</h2>
            <p class="mt-3">
                In geval van een onbevredigend antwoord of geen antwoord binnen dertig dagen op de melding of het verzoek, kan de betrokken persoon
                een melding indienen volgens de procedure die op de institutionele website van AgID is aangegeven.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Informatie over de site</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Publicatiedatum van de website:</dt>
                    <dd class="sm:col-span-2">
                        <time datetime="2024-08-27" class="font-bold">27 augustus 2024</time>
                    </dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Testen op bruikbaarheid zijn uitgevoerd:</dt>
                    <dd class="font-bold sm:col-span-2">nee</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">CMS gebruikt voor de website:</dt>
                    <dd class="font-bold sm:col-span-2">Eigen/custom ontwikkelde platform</dd>
                </div>
            </dl>
        </section>

        <section class="mb-4">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Informatie over de structuur</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Het aantal werknemers met een handicap binnen de administratie:</dt>
                    <dd class="font-bold sm:col-span-2">1</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Het aantal werkplekken voor werknemers met een handicap:</dt>
                    <dd class="font-bold sm:col-span-2">0</dd>
                </div>
            </dl>
        </section>
    </ContentPage>
</template>
